import { useList } from '@refinedev/core';
import { Field, TextInput } from './Form.js';

interface OwnerOption {
  id: string;
  displayName: string;
  email: string;
}

export interface OwnerAssignment {
  ownerId: string;
  percent: string;
  startDate: string;
}

export const EMPTY_OWNER_ASSIGNMENT: OwnerAssignment = {
  ownerId: '',
  percent: '100',
  startDate: new Date().toISOString().slice(0, 10),
};

interface PropertyOwnerPickerProps {
  value: OwnerAssignment;
  onChange: (value: OwnerAssignment) => void;
}

/**
 * Optionally assign the property's owner at creation time.
 *
 * Creates the same `Ownership` row PropertyShow.tsx's "Add owner" button
 * creates after the fact (see OwnershipForm.tsx) — the row that links a
 * property to an owner and is what grants that owner portal access. Queued
 * here the same way PropertyPhotoQueue.tsx queues photos: there is no
 * propertyId to attach it to until the property is saved, so
 * PropertyForm.tsx submits it right after.
 *
 * A single owner at a chosen share, covering the common case of one owner
 * per property. A split across several owners still goes through the
 * existing "Add owner" flow on the property's own page once it exists —
 * that page already computes how much of the 100% remains unallocated,
 * which has no meaning yet for a property with no ownership history.
 */
export function PropertyOwnerPicker({ value, onChange }: PropertyOwnerPickerProps) {
  const { data: ownersData, isLoading } = useList<OwnerOption>({
    resource: 'owners',
    pagination: { current: 1, pageSize: 200 },
  });
  const owners = ownersData?.data ?? [];

  const set =
    <K extends keyof OwnerAssignment>(key: K) =>
    (next: OwnerAssignment[K]) =>
      onChange({ ...value, [key]: next });

  return (
    <div className="card stack">
      <p className="form-section-label">Owner</p>
      <p className="muted">
        Grants portal access to this property from the start date below. <br />
        Adding more owners can be done from the property view after this step.
      </p>

      <Field label="Owner" name="ownerAssignmentId">
        <select
          id="ownerAssignmentId"
          name="ownerAssignmentId"
          className="field__input"
          value={value.ownerId}
          onChange={(event) => set('ownerId')(event.target.value)}
          disabled={isLoading}
        >
          <option value="">{isLoading ? 'Loading owners…' : "Not yet — I'll add one later"}</option>
          {owners.map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.displayName} · {owner.email}
            </option>
          ))}
        </select>
      </Field>

      {owners.length === 0 && !isLoading ? (
        <p className="muted">
          No owners on file yet — add one from the property's page once it's created.
        </p>
      ) : null}

      {value.ownerId ? (
        <>
          <Field label="Share (%)" name="ownerAssignmentPercent" required>
            <TextInput
              name="ownerAssignmentPercent"
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={value.percent}
              onChange={set('percent')}
              required
            />
          </Field>

          <Field
            label="Start date"
            name="ownerAssignmentStartDate"
            required
            hint="Portal access begins on this date."
          >
            <TextInput
              name="ownerAssignmentStartDate"
              type="date"
              value={value.startDate}
              onChange={set('startDate')}
              required
            />
          </Field>
        </>
      ) : null}
    </div>
  );
}
